import * as _ from "lodash";
import * as React from "react";
import { HashMap } from "../../shared/model/HashMap";
import { Option } from "../../shared/model/Option";
import { Utilities } from "../../shared/utilities";
import { ERuleStatus, IAppReducerAction, IEnvironmentData, IRuleData, IRuleDataPoint, IRuleDataSeries, RuleTimeZone } from "../shared/model";
import Time from "shared/Time";

export const getEnvironmentsAndPromotedVariables = async (dispatch: React.Dispatch<IAppReducerAction>) => {
    dispatch({ type: "SET_IS_LOADING", isLoading: true });
    const fetchEnvironments = async () => {
        try {
            const res: IEnvironmentData[] = await Utilities.getFetch("api/monitoringRules/getEnvironments");
            dispatch({ type: "SET_ENVIRONMENTS", environments: res });
        } catch (e) {
            Utilities.toastError(e);
            console.error(e);
        }
    };
    const fetchPromotedVariables = async () => {
        try {
            const res: string[] = await Utilities.getFetch("api/monitoringRules/getPromotedVariables");
            const resToOptions: Option[] = res.map((x) => {
                return {
                    value: x,
                    label: x
                };
            });
            dispatch({ type: "SET_PROMOTED_VARIABLES", promotedVariables: resToOptions });
        } catch (e) {
            Utilities.toastError(e);
            console.error(e);
        }
    };
    await Promise.all([fetchEnvironments(), fetchPromotedVariables()]).then(() => {
        dispatch({ type: "SET_IS_LOADING", isLoading: false });
    });
};

export const getTimeZones = (dispatch: React.Dispatch<IAppReducerAction>) => {
    const fetchTimeZones = async () => {
        try {
            const res: RuleTimeZone[] = await Utilities.getFetch("api/monitoringRules/getTimeZones");
            dispatch({ type: "SET_TIME_ZONES", timeZones: res });
        } catch (e) {
            Utilities.toastError(e);
            console.error(e);
        }
    };
    fetchTimeZones();
};

export const getRules = (dispatch: React.Dispatch<IAppReducerAction>, environmentName: string) => {
    dispatch({ type: "SET_IS_LOADING", isLoading: true });
    const getLastStatus = (series: IRuleDataSeries[]) => {
        if (!series || _.isEmpty(series)) {
            return ERuleStatus.Unknown;
        }

        if (_.some(series, (s) => s.alert && !s.alert.acknowledged)) {
            return ERuleStatus.Alert;
        }

        return ERuleStatus.Ok;
    };
    const getDescription = (rule: IRuleData) => {
        const timeperiodDescription = !rule.config.isActiveEntireDay || !rule.config.isActiveEveryDay ? FRENDS.Localizations.OnSelectedTimes.toLowerCase() : "";
        return `${FRENDS.Localizations.RuleForVariable} '${rule.config.variableName}' ${timeperiodDescription} `;
    };

    const operators: HashMap<string> = {
        GreaterThan: ">",
        LessThan: "<",
        Equal: "=",
        Contains: FRENDS.Localizations.Contains
    };

    const getValueChartData = (rule: IRuleData): IRuleDataPoint[] => {

        // Find first index where there are actual values to not render useless data.
        const firstIndexWithValues = rule.series.findIndex((ruleSeries) => ruleSeries.value != null || ruleSeries.alert != null);
        if (firstIndexWithValues === -1) {
            return [];
        }
        const dataSeries = rule.series.slice(firstIndexWithValues).map((s) => {
            return {
                x: Time.toLocal(s.timestamp).getTime(),
                series: s.value,
                seriesLabel: `Value was ${s.value}`,
                alert: !_.isEmpty(s.alert) ? (s?.value ?? 0) : null,
                alertLabel: `${FRENDS.Localizations.ExpectedValue} ${operators[rule.config.operator]} ${rule.config.expectedValue}, ${FRENDS.Localizations.Was.toLowerCase()} ${s.alert ? (s.value || 0) : null}`
            };
        });
        return _.sortBy(dataSeries, (x) => x.x);
    };



    const fetchRules = async () => {
        try {
            const res: IRuleData[] = await Utilities.getFetch(`api/monitoringRules/getRules/${environmentName}`);
            res.forEach((x) => {
                x["lastStatus"] = getLastStatus(x.series);
                x["description"] = getDescription(x);
                x["seriesDataAvailable"] = x.series && !_.isEmpty(x.series);
                x["chartData"] = x.series && getValueChartData(x);
            });
            dispatch({ type: "SET_RULES", rules: res });
        } catch (e) {
            Utilities.toastError(e);
            console.error(e);
        }
    };
    fetchRules();
    dispatch({ type: "SET_IS_LOADING", isLoading: false });
};
